/**
 * The conversation about one photograph.
 *
 * The transcript lives on the photo, the proxy sent for analysis is the frame as
 * it currently looks, and the numbers that travel with it are the ones the
 * histogram is drawn from. The assistant proposes; the plan under its reply is
 * where the user decides.
 */

import { useEffect, useRef, useState } from 'react';
import {
  type ChatMessage,
  describeActiveRecipe,
  formatCapture,
  summariseStatsForPlanner,
  validatePlan,
} from '@lumos/core';
import { useSelectedPhoto, useStore, type Photo } from '../state/store.ts';
import {
  MAX_REFERENCES,
  REFERENCE_EDGE,
  type HealthReport,
  type PlanCapture,
  type PlanHistoryTurn,
  type ReferenceMode,
  checkHealth,
  fileToDataUrl,
  requestPlan,
  storeAccessCode,
  storedAccessCode,
  type PlannerTier,
} from '../ai/client.ts';
import { captureProxy } from './canvasBridge.ts';
import { Segmented } from './controls/Panel.tsx';
import { Tip } from './controls/Tooltip.tsx';
import { PlanReview } from './PlanReview.tsx';

/** Openers a photographer would actually type. */
const SUGGESTIONS = [
  'Fix the white balance',
  'Open up the shadows',
  'Pull back the highlights',
  'Make it feel like late afternoon',
  'Match my reference',
  'Why does the skin look red?',
];

const WHAT_IS_SENT =
  'Sends a small preview of this photo, its measurements, and your current settings.';

/** How much of the transcript travels with each request. */
const HISTORY_TURNS = 12;

/** How long the Clear button stays primed before it forgets it was pressed. */
const CONFIRM_MS = 3000;

interface Reference {
  id: string;
  name: string;
  dataUrl: string;
}

export function Chat() {
  const photo = useSelectedPhoto();
  const busy = useStore((state) => state.chatBusy);

  const [draft, setDraft] = useState('');
  const [references, setReferences] = useState<Reference[]>([]);
  const [referenceMode, setReferenceMode] = useState<ReferenceMode>('inspire');
  const [tier, setTier] = useState<PlannerTier>('quick');
  const [health, setHealth] = useState<HealthReport | null>(null);
  const [accessCode, setAccessCode] = useState(() => storedAccessCode());
  const [model, setModel] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [showSent, setShowSent] = useState(false);

  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const confirmTimer = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void checkHealth().then((report) => {
      if (cancelled) return;
      setHealth(report);
      if (report.model) setModel(report.model);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const photoId = photo?.id ?? null;
  const turnCount = photo?.chat.length ?? 0;

  useEffect(() => {
    const node = logRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [photoId, turnCount, busy]);

  // A primed Clear button must not follow the user to the next photograph.
  useEffect(() => {
    window.clearTimeout(confirmTimer.current);
    setConfirmClear(false);
  }, [photoId]);

  useEffect(() => () => window.clearTimeout(confirmTimer.current), []);

  // The composer grows with the message and stops at the height the stylesheet
  // allows, so a long instruction stays readable while typing.
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`;
  }, [draft]);

  if (!photo) return null;

  const ready = photo.status === 'ready';
  const canSend = ready && !busy && draft.trim().length > 0;

  const clear = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      confirmTimer.current = window.setTimeout(() => setConfirmClear(false), CONFIRM_MS);
      return;
    }
    window.clearTimeout(confirmTimer.current);
    setConfirmClear(false);
    useStore.getState().clearChat();
  };

  const addReferences = async (files: FileList | null) => {
    const store = useStore.getState();
    if (!files || files.length === 0) return;
    const room = MAX_REFERENCES - references.length;
    if (room <= 0) {
      store.notify(`References are capped at ${MAX_REFERENCES} images.`, 'error');
      return;
    }

    const added: Reference[] = [];
    for (const file of Array.from(files).slice(0, room)) {
      try {
        added.push({
          id: newId(),
          name: file.name,
          dataUrl: await fileToDataUrl(file, REFERENCE_EDGE),
        });
      } catch (error) {
        store.notify(error instanceof Error ? error.message : `Could not read ${file.name}.`, 'error');
      }
    }
    if (added.length > 0) setReferences((current) => [...current, ...added]);
  };

  const send = async () => {
    const text = draft.trim();
    const store = useStore.getState();
    if (text.length === 0 || store.chatBusy) return;
    if (!ready) {
      store.notify('Wait for this photo to finish decoding.', 'error');
      return;
    }

    const attached = references.map((reference) => reference.dataUrl);
    const history = toHistory(photo.chat);

    setDraft('');
    setReferences([]);

    store.appendChat(photo.id, {
      id: newId(),
      role: 'user',
      text,
      referenceThumbs: attached.length > 0 ? attached : undefined,
      createdAt: Date.now(),
    });
    store.setChatBusy(true);

    try {
      const proxy = await captureProxy();
      // The proxy comes from the canvas, so it is only this photo's frame for as
      // long as this photo is the selected one.
      if (useStore.getState().selectedId !== photo.id) {
        throw new Error(
          'The selection moved to another photo while this frame was being prepared, so nothing was sent. Send it again.',
        );
      }
      const current = useStore.getState().photos.find((entry) => entry.id === photo.id);
      if (!current) return;
      const stats = useStore.getState().stats;

      const response = await requestPlan({
        tier,
        message: text,
        imageDataUrl: proxy.current,
        originalDataUrl: proxy.original,
        referenceDataUrls: attached,
        referenceMode: attached.length > 0 ? referenceMode : 'inspire',
        stats: stats ? summariseStatsForPlanner(stats) : null,
        activeRecipe: describeActiveRecipe(current.recipe),
        capture: captureFor(current),
        history,
      });

      setModel(response.model);

      // Validate against the recipe as it stands now: the user may have moved a
      // slider while the model was thinking, and the delta bounds have to apply
      // to the recipe the plan will actually land on.
      const latest = useStore.getState().photos.find((entry) => entry.id === photo.id) ?? current;
      const { plan, rejections } = validatePlan(response.raw, latest.recipe);
      for (const rejection of rejections) plan.warnings.push({ message: rejection, severity: 'info' });

      useStore.getState().appendChat(photo.id, {
        id: newId(),
        role: 'assistant',
        text: replyOf(response.raw),
        plan,
        disposition: 'pending',
        createdAt: Date.now(),
      });
      useStore.getState().stagePlan(photo.id, plan);
    } catch (error) {
      useStore.getState().appendChat(photo.id, {
        id: newId(),
        role: 'assistant',
        text: '',
        error:
          error instanceof Error
            ? error.message
            : 'The request failed, and the failure carried no message.',
        createdAt: Date.now(),
      });
    } finally {
      useStore.getState().setChatBusy(false);
    }
  };

  return (
    <aside className="chat-column" aria-label="Assistant">
      <div className="chat-head">
        <div className="topbar-spacer" style={{ minWidth: 0 }}>
          <div className="panel-title">Assistant</div>
          <div
            className="muted"
            style={{
              fontSize: 11,
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {plannerLine(health, model)}
          </div>
        </div>

        <Tip title="What gets sent" body={WHAT_IS_SENT}>
          <button
            type="button"
            className="btn tiny quiet"
            aria-expanded={showSent}
            aria-controls="chat-privacy"
            onClick={() => setShowSent((open) => !open)}
          >
            Privacy
          </button>
        </Tip>

        <button
          type="button"
          className="btn tiny"
          disabled={busy || turnCount === 0}
          aria-label={confirmClear ? 'Confirm clearing this conversation' : 'Clear this conversation'}
          onClick={clear}
        >
          {confirmClear ? 'Clear?' : 'Clear'}
        </button>
      </div>

      {showSent ? (
        <div className="slider-help" id="chat-privacy" style={{ padding: '8px 14px' }}>
          {WHAT_IS_SENT}
        </div>
      ) : null}

      {health?.requiresAccessCode ? (
        <div className="chat-head">
          <label className="field topbar-spacer" htmlFor="chat-access-code">
            <Tip
              title="Access code"
              body="This copy keeps the assistant behind a shared code. It stays in this browser."
            >
              <span className="label">Access code</span>
            </Tip>
            <input
              id="chat-access-code"
              type="password"
              value={accessCode}
              placeholder="Shared code"
              autoComplete="off"
              style={{
                height: 28,
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--cork)',
                padding: '0 2px',
              }}
              onChange={(event) => {
                setAccessCode(event.target.value);
                storeAccessCode(event.target.value.trim());
              }}
            />
          </label>
        </div>
      ) : null}

      <div
        className="chat-log"
        ref={logRef}
        role="log"
        tabIndex={0}
        aria-label="Conversation about this photo"
      >
        {photo.chat.length === 0 ? (
          <div className="suggestions" role="group" aria-label="Suggested openers">
            {SUGGESTIONS.map((suggestion) => (
              <button
                type="button"
                className="suggestion"
                key={suggestion}
                disabled={busy}
                onClick={() => {
                  setDraft(suggestion);
                  inputRef.current?.focus();
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        ) : null}

        {photo.chat.map((message) => (
          <Turn key={message.id} photoId={photo.id} message={message} />
        ))}

        {busy ? (
          <div className="msg assistant" role="status">
            <div className="chat-actions">
              <span className="spinner" aria-hidden="true" />
              <span className="muted">Looking at the photo</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="chat-compose">
        {references.length > 0 ? (
          <div className="ref-strip">
            {references.map((reference) => (
              <div className="ref-thumb" key={reference.id}>
                <img src={reference.dataUrl} alt={`Reference: ${reference.name}`} />
                <button
                  type="button"
                  aria-label={`Remove reference ${reference.name}`}
                  onClick={() =>
                    setReferences((current) => current.filter((entry) => entry.id !== reference.id))
                  }
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <label className="label" htmlFor="chat-input">
          Message
        </label>
        <textarea
          id="chat-input"
          ref={inputRef}
          className="chat-input"
          value={draft}
          placeholder="What do you want changed?"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey) return;
            if (event.nativeEvent.isComposing) return;
            event.preventDefault();
            void send();
          }}
        />

        <div className="chat-actions">
          <Tip title="Reference" body={`Attach up to ${MAX_REFERENCES} photos to steer the look.`}>
            <button
              type="button"
              className="btn tiny"
              disabled={busy || references.length >= MAX_REFERENCES}
              onClick={() => fileRef.current?.click()}
            >
              Reference{references.length > 0 ? ` ${references.length}/${MAX_REFERENCES}` : ''}
            </button>
          </Tip>

          {references.length > 0 ? (
            <Tip
              as="div"
              title="Inspire or match"
              body="Inspire borrows the feel. Match aims for the same tone and colour, as near as this file gets."
            >
              <Segmented<ReferenceMode>
                value={referenceMode}
                onChange={setReferenceMode}
                ariaLabel="How to use the reference images"
                options={[
                  { value: 'inspire', label: 'Inspire' },
                  { value: 'match', label: 'Match' },
                ]}
              />
            </Tip>
          ) : null}

          <Tip
            as="div"
            title="Quick or considered"
            body="Quick answers in seconds. Considered takes about half a minute and reasons harder."
          >
            <Segmented<PlannerTier>
              value={tier}
              onChange={setTier}
              ariaLabel="How long the planner thinks"
              options={[
                { value: 'quick', label: 'Quick' },
                { value: 'considered', label: 'Considered' },
              ]}
            />
          </Tip>

          <span className="topbar-spacer" />
          <button type="button" className="btn primary" disabled={!canSend} onClick={() => void send()}>
            Send
          </button>
        </div>

        {!ready ? (
          <div className="slider-help">
            {photo.status === 'error'
              ? 'This photo did not decode, so there is nothing to look at.'
              : 'Ready once this photo finishes decoding.'}
          </div>
        ) : null}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          aria-label="Attach reference images"
          onChange={(event) => {
            void addReferences(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
    </aside>
  );
}

function Turn({ photoId, message }: { photoId: string; message: ChatMessage }) {
  const isUser = message.role === 'user';
  // The plan already states its read of the photograph; repeating it above the
  // proposal would just be the same sentence twice.
  const body =
    message.plan && message.text.trim() === message.plan.summary.trim() ? '' : message.text;

  return (
    <div className={`msg ${isUser ? 'user' : 'assistant'}`}>
      <div className="msg-role">{isUser ? 'You' : 'Assistant'}</div>

      {body.length > 0 ? (
        <div className="msg-body">
          {body.split('\n').map((line, index) => (
            <div key={index}>{line.length > 0 ? line : ' '}</div>
          ))}
        </div>
      ) : null}

      {message.referenceThumbs && message.referenceThumbs.length > 0 ? (
        <div className="ref-strip" style={{ marginTop: 7 }}>
          {message.referenceThumbs.map((source, index) => (
            <div className="ref-thumb" key={index}>
              <img src={source} alt={`Reference image ${index + 1} sent with this message`} />
            </div>
          ))}
        </div>
      ) : null}

      {message.error ? <div className="msg-error">{message.error}</div> : null}

      {message.plan ? (
        <div style={{ marginTop: 8 }}>
          <PlanReview photoId={photoId} plan={message.plan} disposition={message.disposition} />
        </div>
      ) : null}
    </div>
  );
}

/** One muted line under the title. The model, or why there is not one. */
function plannerLine(health: HealthReport | null, model: string | null): string {
  if (!health) return 'Checking the connection';
  if (!health.reachable) return health.detail ?? 'The Lumos server is not answering';
  if (!health.configured) return health.detail ?? 'No model key on the server';
  return model ?? 'Connected';
}

function captureFor(photo: Photo): PlanCapture | null {
  const image = photo.image;
  if (!image) return null;
  return {
    ...image.capture,
    filename: photo.filename,
    summary: formatCapture(image.capture),
    fullWidth: image.fullWidth,
    fullHeight: image.fullHeight,
    source: image.kind,
    fromEmbeddedPreview: image.fromEmbeddedPreview,
    asShot: image.asShot,
  };
}

function toHistory(chat: ChatMessage[]): PlanHistoryTurn[] {
  return chat.slice(-HISTORY_TURNS).map((message) => ({
    role: message.role,
    text: message.error
      ? `(this turn failed: ${message.error})`
      : message.text || message.plan?.summary || '',
    proposed: message.plan?.operations.map((operation) => operation.op),
    disposition: message.disposition,
  }));
}

/**
 * The conversational half of a planner reply, when it sent one. The plan's own
 * summary is shown by the review panel, so an empty string here is normal.
 */
function replyOf(raw: unknown): string {
  if (typeof raw !== 'object' || raw === null) return '';
  const source = raw as Record<string, unknown>;
  for (const key of ['reply', 'message', 'answer'] as const) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return '';
}

let counter = 0;

function newId(): string {
  counter += 1;
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `msg-${counter}-${random}`;
}

export default Chat;
